"use client";

import { useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import type { ResearchStudy, StudyField, StudyType } from "@/lib/research/types";
import { filterStudies } from "@/lib/research/filters";
import { getReferencePeptideBySlug } from "@/lib/research/peptides-reference";
import { CornerMark } from "@/ui/components/research/corner-mark";
import { StudyCard } from "@/ui/components/research/study-card";

const TYPE_ORDER: StudyType[] = [
	"rct-3",
	"rct-2",
	"rct-1",
	"rct-4",
	"case-series",
	"observational",
	"animal",
	"in-vitro",
	"review",
	"meta",
];

const FIELD_ORDER: StudyField[] = [
	"metabolic",
	"wound",
	"cosmetic",
	"cns",
	"longevity",
	"wellness",
	"pharma",
	"immune",
];

interface JournalFilterProps {
	studies: ResearchStudy[];
	locale: string;
}

export function JournalFilter({ studies, locale }: JournalFilterProps) {
	const t = useTranslations("research");
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const types = useMemo(() => {
		const raw = searchParams.get("types");
		return raw ? (raw.split(",").filter(Boolean) as StudyType[]) : [];
	}, [searchParams]);

	const fields = useMemo(() => {
		const raw = searchParams.get("fields");
		return raw ? (raw.split(",").filter(Boolean) as StudyField[]) : [];
	}, [searchParams]);

	const peptideSlug = searchParams.get("peptide") ?? undefined;
	const humanOnly = searchParams.get("human") === "1";
	const query = searchParams.get("q") ?? "";

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			const params = new URLSearchParams(searchParams.toString());
			for (const [key, value] of Object.entries(updates)) {
				if (value === null || value === "") {
					params.delete(key);
				} else {
					params.set(key, value);
				}
			}
			const qs = params.toString();
			router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
		},
		[pathname, router, searchParams],
	);

	const toggleInList = <T extends string>(list: T[], value: T): T[] =>
		list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

	const filtered = useMemo(() => {
		let result = filterStudies(studies, {
			types: types.length > 0 ? types : undefined,
			fields: fields.length > 0 ? fields : undefined,
			peptideSlug,
			humanOnly,
		});

		const q = query.trim().toLowerCase();
		if (q) {
			result = result.filter((s) => {
				const hay = [s.title, s.authors, s.journal, ...s.peptideSlugs, t(`studies.${s.id}.finding`)]
					.join(" ")
					.toLowerCase();
				return hay.includes(q);
			});
		}
		return result;
	}, [studies, types, fields, peptideSlug, humanOnly, query, t]);

	const peptideOptions = useMemo(() => {
		const slugs = new Set<string>();
		for (const s of studies) {
			for (const slug of s.peptideSlugs) slugs.add(slug);
		}
		return [...slugs].sort();
	}, [studies]);

	const activeCount =
		types.length + fields.length + (humanOnly ? 1 : 0) + (query ? 1 : 0) + (peptideSlug ? 1 : 0);

	const reset = () => {
		router.replace(pathname, { scroll: false });
	};

	const availableTypes = TYPE_ORDER.filter((type) => studies.some((s) => s.type === type));

	return (
		<section className="bg-background py-16">
			<div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="grid gap-6 lg:grid-cols-[300px_1fr] lg:gap-10">
					<aside className="lg:sticky lg:top-32 lg:self-start">
						<div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
							<CornerMark corner="top-left" className="absolute left-2 top-2 opacity-40" />
							<CornerMark corner="top-right" className="absolute right-2 top-2 opacity-40" />

							<div className="relative">
								<div className="flex items-center justify-between gap-3 px-5 pb-3 pt-5">
									<div className="flex items-center gap-2 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-primary">
										<span className="relative flex h-1.5 w-1.5">
											<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
										</span>
										{t("journal.filterEyebrow")}
									</div>
									{activeCount > 0 && (
										<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 font-mono text-[0.58rem] font-medium tabular-nums text-primary-foreground">
											{activeCount}
										</span>
									)}
								</div>

								<div className="border-border/80 border-b px-5 pb-5">
									<label className="block">
										<span className="mb-2 block font-mono text-[0.55rem] uppercase tracking-[0.2em] text-muted-foreground">
											{t("journal.searchLabel")}
										</span>
										<input
											type="search"
											value={query}
											onChange={(e) => updateParams({ q: e.target.value || null })}
											placeholder={t("journal.searchPlaceholder")}
											className="focus:ring-primary/10 w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2"
										/>
									</label>
								</div>

								<div className="border-border/80 bg-muted/30 border-b px-5 py-4">
									<label className="flex cursor-pointer items-center gap-3">
										<input
											type="checkbox"
											checked={humanOnly}
											onChange={() => updateParams({ human: humanOnly ? null : "1" })}
											className="sr-only"
										/>
										<span
											className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
												humanOnly ? "border-primary bg-primary" : "border-border bg-background"
											}`}
											aria-hidden
										>
											{humanOnly && (
												<svg
													viewBox="0 0 12 12"
													className="h-2.5 w-2.5 text-primary-foreground"
													fill="none"
													stroke="currentColor"
													strokeWidth="2.4"
												>
													<path d="M2.5 6.5 L5 9 L9.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											)}
										</span>
										<span className="font-mono text-[0.62rem] font-medium uppercase tracking-[0.16em] text-primary">
											{t("journal.humanOnly")}
										</span>
									</label>
								</div>

								{peptideOptions.length > 0 && (
									<FilterGroup index="00" label={t("journal.filterPeptide")}>
										<select
											value={peptideSlug ?? ""}
											onChange={(e) => updateParams({ peptide: e.target.value || null })}
											className="focus:ring-primary/10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2"
										>
											<option value="">{t("journal.filterPeptideAll")}</option>
											{peptideOptions.map((slug) => {
												const ref = getReferencePeptideBySlug(slug);
												return (
													<option key={slug} value={slug}>
														{ref?.name ?? slug}
													</option>
												);
											})}
										</select>
									</FilterGroup>
								)}

								<FilterGroup index="01" label={t("journal.filterField")}>
									{FIELD_ORDER.filter((f) => studies.some((s) => s.fields.includes(f))).map((field) => (
										<FilterToggle
											key={field}
											checked={fields.includes(field)}
											onChange={() => {
												const next = toggleInList(fields, field);
												updateParams({ fields: next.length > 0 ? next.join(",") : null });
											}}
											label={t(`studyFields.${field}`)}
											count={studies.filter((s) => s.fields.includes(field)).length}
										/>
									))}
								</FilterGroup>

								<FilterGroup index="02" label={t("journal.filterType")}>
									{availableTypes.map((type) => (
										<FilterToggle
											key={type}
											checked={types.includes(type)}
											onChange={() => {
												const next = toggleInList(types, type);
												updateParams({ types: next.length > 0 ? next.join(",") : null });
											}}
											label={t(`studyTypes.${type}`)}
											count={studies.filter((s) => s.type === type).length}
										/>
									))}
								</FilterGroup>

								{activeCount > 0 && (
									<div className="border-border/80 border-t px-5 py-3">
										<button
											type="button"
											onClick={reset}
											className="border-primary/30 w-full rounded-full border px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-primary transition-colors hover:bg-muted"
										>
											{t("journal.resetFilters")}
										</button>
									</div>
								)}
							</div>
						</div>
					</aside>

					<div>
						<div className="mb-6 flex flex-wrap items-baseline justify-between gap-3 border-b border-border pb-4">
							<div>
								<div className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground">
									{t("journal.resultsLabel")}
								</div>
								<div className="mt-1 font-display text-[1.4rem] font-medium tracking-tight">
									{filtered.length}{" "}
									<span className="text-[1rem] text-muted-foreground">
										/ {studies.length} {t("journal.resultsOf")}
									</span>
								</div>
							</div>
						</div>

						{filtered.length === 0 ? (
							<div className="bg-muted/30 rounded-2xl border border-dashed border-border px-6 py-16 text-center">
								<div className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground">
									{t("journal.noResults")}
								</div>
								<p className="mx-auto mt-3 max-w-md text-muted-foreground">{t("journal.noResultsHint")}</p>
								<button
									type="button"
									onClick={reset}
									className="hover:bg-primary/90 mt-6 inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-[0.7rem] font-medium uppercase tracking-[0.16em] text-primary-foreground transition-colors"
								>
									{t("journal.resetFilters")}
								</button>
							</div>
						) : (
							<div className="space-y-4">
								{filtered.map((study) => (
									<StudyCard key={study.id} study={study} locale={locale} />
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}

function FilterGroup({
	index,
	label,
	children,
}: {
	index: string;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="border-border/80 border-b px-5 py-4">
			<div className="mb-3 flex items-baseline gap-2.5">
				<span className="text-primary/70 font-mono text-[0.52rem] tabular-nums tracking-[0.2em]">
					{index}
				</span>
				<span className="font-mono text-[0.58rem] font-medium uppercase tracking-[0.22em] text-muted-foreground">
					{label}
				</span>
				<span aria-hidden className="ml-1 h-px flex-1 bg-border" />
			</div>
			<div className="flex flex-col gap-0.5">{children}</div>
		</div>
	);
}

function FilterToggle({
	checked,
	onChange,
	label,
	count,
}: {
	checked: boolean;
	onChange: () => void;
	label: string;
	count?: number;
}) {
	return (
		<label
			className={`group -mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors ${
				checked ? "bg-muted" : "hover:bg-muted/50"
			}`}
		>
			<input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
			<span
				className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
					checked ? "border-primary bg-primary" : "group-hover:border-primary border-border bg-background"
				}`}
				aria-hidden
			>
				{checked && (
					<svg
						viewBox="0 0 12 12"
						className="h-2.5 w-2.5 text-primary-foreground"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.4"
					>
						<path d="M2.5 6.5 L5 9 L9.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				)}
			</span>
			<span
				className={`flex-1 text-[0.82rem] transition-colors ${checked ? "font-medium text-foreground" : "text-muted-foreground"}`}
			>
				{label}
			</span>
			{typeof count === "number" && (
				<span
					className={`font-mono text-[0.56rem] tabular-nums tracking-[0.14em] ${checked ? "text-primary" : "text-muted-foreground"}`}
				>
					{count.toString().padStart(2, "0")}
				</span>
			)}
		</label>
	);
}
